import { Wallet as CardIconWallet } from 'lucide-react';
import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { Input, Select, Switch, useSnackbar } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import {
  getApiError,
  salesApi,
  type SalePaymentMethod,
  type SalesSettings,
} from '../../lib/api.js';
import { PageHeader } from '../../app/page-title.js';

const METHODS: Array<{ value: SalePaymentMethod; label: string }> = [
  { value: 'CASH', label: 'Dinheiro' },
  { value: 'PIX', label: 'PIX' },
  { value: 'CARD', label: 'Cartão' },
  { value: 'OTHER', label: 'Outro' },
];

/// Em quais meios a confirmação manual do pagamento exige anexar o comprovante (ADR 0053). Cada
/// chave grava na hora: é uma escolha por vez, sem formulário a confirmar.
export function CompanySalesSettings({
  units = [],
}: {
  units?: Array<{ id: string; displayName: string }>;
}) {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [unitSearch, setUnitSearch] = useState('');
  const query = useQuery({
    queryKey: ['company', 'sales', 'settings'],
    queryFn: () => salesApi.settings(),
    retry: false,
  });

  async function toggle(method: SalePaymentMethod, required: boolean) {
    const current = query.data?.receiptRequiredMethods ?? [];
    setSaving(true);
    setError(undefined);
    try {
      const saved = await salesApi.updateSettings({
        ...query.data,
        receiptRequiredMethods: required
          ? [...current, method]
          : current.filter((item) => item !== method),
      });
      client.setQueryData(['company', 'sales', 'settings'], saved);
      snackbar.success('Configuração salva.');
    } catch (value) {
      setError(getApiError(value));
    } finally {
      setSaving(false);
    }
  }

  async function saveCredit(value: Partial<SalesSettings>) {
    setSaving(true);
    setError(undefined);
    try {
      const saved = await salesApi.updateSettings({
        receiptRequiredMethods: [],
        ...query.data,
        ...value,
      });
      client.setQueryData(['company', 'sales', 'settings'], saved);
      snackbar.success('Configuração salva.');
    } catch (value) {
      setError(getApiError(value));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="max-w-2xl">
      <PageHeader
        trail="Configurações / Comprovantes de pagamento"
        title="Comprovantes de pagamento"
        description="Escolha em quais meios a confirmação manual de um pagamento exige anexar o comprovante. Quem confirmou e quando ficam sempre registrados na auditoria."
      />
      <div className="grid gap-4">
        {query.isLoading ? (
          <LoadingCard label="Carregando configuração de vendas" />
        ) : query.error ? (
          <ErrorCard message={getApiError(query.error)} />
        ) : (
          <ul className="divide-y divide-surface-border rounded-2xl border border-surface-border bg-surface-card px-4">
            {METHODS.map(({ value, label }) => {
              const required = query.data?.receiptRequiredMethods.includes(value) ?? false;
              return (
                <li key={value} className="py-3">
                  <Switch
                    checked={required}
                    disabled={saving}
                    onChange={(next) => void toggle(value, next)}
                  >
                    <span>
                      <span className="block font-medium">{label}</span>
                      <span className="block text-xs text-content-muted">
                        {required ? 'Exige comprovante ao confirmar' : 'Comprovante opcional'}
                      </span>
                    </span>
                  </Switch>
                </li>
              );
            })}
          </ul>
        )}
        {query.data ? (
          <section
            className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-4"
            aria-label="Crédito do cliente"
          >
            <ContentCardHeader
              title="Crédito do cliente"
              icon={<CardIconWallet className="size-4" />}
            />
            <p className="text-sm text-content-muted">
              Cancelamento de atendimento com venda converte os recebimentos confirmados em crédito.
              A recepção escolhe quanto usar em Pagamentos.
            </p>
            <Switch
              checked={query.data.creditAcrossUnits ?? false}
              disabled={saving}
              onChange={(creditAcrossUnits) => void saveCredit({ creditAcrossUnits })}
            >
              Permitir crédito de outras unidades — padrão da empresa
            </Switch>
            {units.length > 1 ? (
              <Input
                label="Buscar unidade"
                value={unitSearch}
                onChange={(event) => setUnitSearch(event.target.value)}
              />
            ) : null}
            {units
              .filter((unit) =>
                unit.displayName
                  .toLocaleLowerCase('pt-BR')
                  .includes(unitSearch.toLocaleLowerCase('pt-BR')),
              )
              .map((unit) => {
                const override = query.data?.creditUnitOverrides?.find(
                  (item) => item.businessUnitId === unit.id,
                );
                return (
                  <Select
                    key={unit.id}
                    label={`Crédito em ${unit.displayName}`}
                    value={override ? String(override.creditAcrossUnits) : 'inherit'}
                    disabled={saving}
                    options={[
                      { value: 'inherit', label: 'Usar padrão da empresa' },
                      { value: 'false', label: 'Somente crédito desta unidade' },
                      { value: 'true', label: 'Aceitar crédito de qualquer unidade da empresa' },
                    ]}
                    onValueChange={(value) => {
                      const rest =
                        query.data?.creditUnitOverrides?.filter(
                          (item) => item.businessUnitId !== unit.id,
                        ) ?? [];
                      void saveCredit({
                        creditUnitOverrides:
                          value === 'inherit'
                            ? rest
                            : [
                                ...rest,
                                { businessUnitId: unit.id, creditAcrossUnits: value === 'true' },
                              ],
                      });
                    }}
                  />
                );
              })}
          </section>
        ) : null}
        {error ? <RequestAlert message={error} /> : null}
      </div>
    </section>
  );
}
